/**
 * wait Tool - Idiomatic job polling (like CLI wait command)
 *
 * Polls job status every 10 seconds until completion, timeout, or failure.
 * Returns immediately with results once job reaches terminal state.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import { isTerminalStatus } from '@runhuman/shared';
import type { JobClient } from '../clients/job-client.js';
import { formatCompletedJob, formatTerminalFailure, isFailedVerdict } from '../utils/format-job-result.js';
import { OUTCOME_BUCKETS_GUIDE } from '../utils/outcome-guide.js';

export interface WaitArgs {
  jobId: string;
  timeoutSeconds?: number;
}

export const waitToolDefinition: Tool = {
  name: 'wait',
  description: `Wait for a QA test job to complete and return results.

Polls job status every 10 seconds until the job reaches a terminal state. The non-terminal \`overdue\` state (tester missed the deadline) is polled through normally — the job may still recover to \`completed\` via a late submission, or transition to \`expired\`.

Terminal outcomes:
- completed: Returns test results and the AI Pass/Fail verdict (\`passStatus\`: pass / fail / not_applicable). \`passStatus: 'fail'\` is surfaced as an error so agents can gate on it.
- cancelled: Cancelled before completion. \`modifier\` explains who/why.
- expired: Tester dropped, was unresponsive, or no tester was available.
- failed: Platform or AI error. \`modifier\` carries the specific cause.

${OUTCOME_BUCKETS_GUIDE}

This tool blocks until terminal state or timeout, just like the CLI wait command.

Usage:
1. Create a job with create_job - this returns a jobId
2. Call wait with that jobId to poll until completion
3. Results are returned when job finishes

Default timeout: 600 seconds (10 minutes)`,
  inputSchema: {
    type: 'object',
    properties: {
      jobId: {
        type: 'string',
        description: 'Job ID to wait for (from create_job)',
      },
      timeoutSeconds: {
        type: 'number',
        description: 'Maximum time to wait in seconds. Default: 600 (10 minutes)',
        minimum: 10,
        maximum: 3600,
      },
    },
    required: ['jobId'],
  },
};


export async function handleWait(
  args: WaitArgs,
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  const { jobId, timeoutSeconds = 600 } = args;
  const pollInterval = 10000;
  const startTime = Date.now();
  const timeout = timeoutSeconds * 1000;

  try {
    while (true) {
      const elapsed = Date.now() - startTime;

      if (elapsed >= timeout) {
        return {
          content: [{
            type: 'text',
            text: `Timeout waiting for job completion

Job ID: ${jobId}
Timeout: ${timeoutSeconds}s
Elapsed: ${Math.floor(elapsed / 1000)}s

The job is still running but exceeded the wait timeout.
You can:
- Call wait again with a longer timeout
- Check status with: get_job(jobId: "${jobId}")`,
          }],
          isError: true,
        };
      }

      const job = await jobClient.getJobStatus(jobId);

      if (isTerminalStatus(job.status)) {
        if (job.status === 'completed') {
          return {
            content: [{ type: 'text', text: formatCompletedJob(jobId, job) }],
            ...(isFailedVerdict(job) ? { isError: true } : {}),
          };
        }

        return {
          content: [{ type: 'text', text: formatTerminalFailure(jobId, job) }],
          isError: true,
        };
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }
  } catch (error) {
    const err = error as Error & { status?: number };

    return {
      content: [{
        type: 'text',
        text: `Failed to wait for job

Job ID: ${jobId}
Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}

Please check:
- Job ID is correct: ${jobId}
- Job exists in your project
- Your PAT is valid (create one at https://runhuman.com/dashboard/settings/tokens)`,
      }],
      isError: true,
    };
  }
}
